// GradientLineChart.jsx
import React from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

// Кастомный Tooltip компонент
const CustomTooltip = ({ active, payload, label, currencyFormat }) => {
    if (active && payload && payload.length) {
        const currentBalance = payload[0].value || 0;
        const firstDataPoint = payload[0].payload.chartData?.[0]?.balance || 0;
        const percentageChange = firstDataPoint > 0 ? ((currentBalance - firstDataPoint) / firstDataPoint * 100) : 0;
        const isPositive = percentageChange >= 0;

        const theme = document.documentElement.getAttribute('data-theme');
        const isLiquidGlass = theme === 'liquid-glass';
        const isSolana = theme === 'solana';
        const isBinance = theme === 'binance';
        
        return (
            <div style={{
                backgroundColor: isLiquidGlass 
                    ? 'rgba(255, 255, 255, 0.9)' 
                    : isSolana || isBinance
                    ? 'rgba(30, 32, 40, 0.95)'
                    : '#ffffff',
                backdropFilter: isLiquidGlass ? 'blur(20px) saturate(200%)' : (isSolana || isBinance) ? 'blur(20px)' : 'none',
                WebkitBackdropFilter: isLiquidGlass ? 'blur(20px) saturate(200%)' : (isSolana || isBinance) ? 'blur(20px)' : 'none',
                border: isLiquidGlass 
                    ? '1px solid rgba(255, 255, 255, 0.7)' 
                    : isSolana
                    ? '1px solid rgba(50, 200, 255, 0.3)'
                    : isBinance
                    ? '1px solid rgba(240, 185, 11, 0.3)'
                    : '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: isLiquidGlass
                    ? '0 0 0 1px rgba(255, 255, 255, 0.8) inset, 0 4px 12px rgba(0, 0, 0, 0.1)'
                    : isSolana
                    ? '0 0 0 1px rgba(50, 200, 255, 0.2) inset, 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 20px rgba(50, 200, 255, 0.2)'
                    : isBinance
                    ? '0 0 0 1px rgba(240, 185, 11, 0.2) inset, 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 20px rgba(240, 185, 11, 0.2)'
                    : '0 10px 25px rgba(0, 0, 0, 0.1)',
                minWidth: '220px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
                <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: isSolana || isBinance ? '#e2e8f0' : '#1e293b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: isSolana ? '#32c8ff' : isBinance ? '#f0b90b' : '#3b82f6',
                        borderRadius: '50%',
                        boxShadow: isSolana 
                            ? '0 0 8px rgba(50, 200, 255, 0.8)' 
                            : isBinance 
                            ? '0 0 8px rgba(240, 185, 11, 0.8)' 
                            : 'none'
                    }}></div>
                    Trading Session
                </div>
                
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: isSolana || isBinance ? '#94a3b8' : '#64748b', fontWeight: '500' }}>
                            Balance:
                        </span>
                        <span style={{ 
                            fontSize: '14px', 
                            fontWeight: '700', 
                            color: isSolana || isBinance ? '#e2e8f0' : '#1e293b',
                            fontVariantNumeric: 'tabular-nums'
                        }}>
                            {currencyFormat} {(currentBalance || 0).toLocaleString()}
                        </span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: isSolana || isBinance ? '#94a3b8' : '#64748b', fontWeight: '500' }}>
                            Change:
                        </span>
                        <span style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: isPositive ? '#10b981' : '#ef4444',
                            fontVariantNumeric: 'tabular-nums'
                        }}>
                            {isPositive ? '+' : ''}{percentageChange.toFixed(1)}%
                        </span>
                    </div>
                    
                    <div style={{ 
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: isSolana || isBinance ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{ fontSize: '11px', color: isSolana || isBinance ? '#94a3b8' : '#94a3b8', fontWeight: '500' }}>
                            Initial:
                        </span>
                        <span style={{ 
                            fontSize: '11px', 
                            color: isSolana || isBinance ? '#94a3b8' : '#94a3b8',
                            fontWeight: '500',
                            fontVariantNumeric: 'tabular-nums'
                        }}>
                            {currencyFormat} {(firstDataPoint || 0).toLocaleString()}
                        </span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

export const GradientLineChart = ({ data, currencyFormat = 'RM' }) => {
    // Добавляем ссылку на полные данные для расчета процентов
    const dataWithRef = data.map(item => ({
        ...item,
        chartData: data
    }));

        const theme = document.documentElement.getAttribute('data-theme');
        const isLiquidGlass = theme === 'liquid-glass';
        const isSolana = theme === 'solana';
        const isBinance = theme === 'binance';
    
    return (
        <ResponsiveContainer 
            width="100%" 
            height={300} 
            style={{ 
                background: 'transparent',
                backgroundColor: 'transparent'
            }}
        >
            <AreaChart 
                data={dataWithRef}
                style={{ 
                    background: 'transparent',
                    backgroundColor: 'transparent'
                }}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            >
                {/* Определение градиента */}
                <defs>
                    {isSolana ? (
                        // Зеленая заливка Solana для темы solana
                        <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="rgba(0, 255, 150, 0.5)" />
                            <stop offset="100%" stopColor="rgba(0, 255, 150, 0)" />
                        </linearGradient>
                    ) : isBinance ? (
                        // Желтая заливка Binance для темы binance
                        <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="rgba(240, 185, 11, 0.5)" />
                            <stop offset="100%" stopColor="rgba(240, 185, 11, 0)" />
                        </linearGradient>
                    ) : (
                        // Стандартный синий градиент
                        <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="rgba(59,130,246,0.5)" />
                            <stop offset="100%" stopColor="rgba(59,130,246,0)" />
                        </linearGradient>
                    )}
                    {isSolana && (
                        // Градиент Solana только для линии графика
                        <linearGradient id="strokeSolana" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="rgba(235, 100, 225, 1)" />
                            <stop offset="50%" stopColor="rgba(50, 200, 255, 1)" />
                            <stop offset="100%" stopColor="rgba(0, 255, 150, 1)" />
                        </linearGradient>
                    )}
                    {isBinance && (
                        // Желтый градиент Binance для линии графика
                        <linearGradient id="strokeBinance" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="rgba(240, 185, 11, 1)" />
                            <stop offset="50%" stopColor="rgba(245, 200, 66, 1)" />
                            <stop offset="100%" stopColor="rgba(240, 185, 11, 1)" />
                        </linearGradient>
                    )}
                </defs>
                {/* Сетка графика */}
                <CartesianGrid 
                    strokeDasharray="3 3" 
                    vertical={false} 
                    stroke={
                        isSolana ? "rgba(50, 200, 255, 0.1)" : 
                        isBinance ? "rgba(240, 185, 11, 0.1)" : 
                        undefined
                    } 
                />
                {/* Ось X */}
                <XAxis dataKey="name" hide />
                {/* Ось Y */}
                <YAxis hide />
                {/* Кастомная всплывающая подсказка */}
                <Tooltip content={<CustomTooltip currencyFormat={currencyFormat} />} />
                {/* Область графика с градиентным фоном */}
                <Area
                    type="linear"
                    dataKey="balance"
                    stroke={
                        isSolana ? "url(#strokeSolana)" : 
                        isBinance ? "url(#strokeBinance)" : 
                        "rgb(59,130,246)"
                    }
                    strokeWidth={2}
                    fill="url(#colorBalance)"
                    dot={false}
                    activeDot={{ 
                        r: 6, 
                        fill: isSolana ? '#32c8ff' : isBinance ? '#f0b90b' : '#3b82f6', 
                        stroke: '#ffffff', 
                        strokeWidth: 2,
                        filter: isSolana 
                            ? 'drop-shadow(0 0 8px rgba(50, 200, 255, 0.8))' 
                            : isBinance 
                            ? 'drop-shadow(0 0 8px rgba(240, 185, 11, 0.8))' 
                            : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};
